import { useTranslations } from '@vocab/react';
import { Box, RadioGroup, RadioItem } from 'braid-design-system';

import { CajaButtons } from '../../../components/CajaButtons';
import Tooltip from '../../../components/DisablePayment/Tooltip';
import { HirerPaymentTerms } from '../../../components/HirerPaymentTerms';
import { PageSection } from '../../../components/PageSection';
import { CreditCard, Invoice } from '../../../components/PaymentMethods';
import Cpqa from '../../../components/PaymentMethods/CPQA';
import TransactionError from '../../../components/TransactionError';
import { PaymentBox } from '../../../components/shared/PaymentBox';
import { usePayments } from '../../../context/PaymentsProvider';
import type { PaymentMethodType } from '../../../entities/enums';
import translations from '../../../locales.vocab';
import useMaintenance from '../../../usecases/Maintenance/useMaintenance';
import { useMobileView } from '../../../usecases/MobileView/useMobileView';
import { getPaymentMethods } from '../../../utils/getPaymentMethods';

const defaultProps = {
  features: {
    creditcard: true,
    invoice: true,
  },
  invoiceOnly: false,
  cpqaOnly: false,
  hideAllPayments: false,
};

const SeekJobsNZ = () => {
  const { t } = useTranslations(translations);
  const isMobileView = useMobileView();
  const {
    paymentMethod,
    invoiceOnly = defaultProps.invoiceOnly,
    hideAllPayments = defaultProps.hideAllPayments,
    isAdBudgetSelected,
    postAdButtonText = t('pay.button.submit.caja'),
    previewButtonComponent,
    backButtonComponent,
    site,
    env,
    isPaymentInProgress,
    errorMessage,
    feature = defaultProps.features,
    handlePayAndPostButtonClick,
    onRadioChange,
    cpqaOnly = defaultProps.cpqaOnly,
    useCase,
  } = usePayments();

  const noFeatureEnabled = Object.values(feature).every((payment) => !payment);
  const hidePaymentOptions = paymentMethod === undefined || noFeatureEnabled;

  if (invoiceOnly || cpqaOnly) {
    feature.creditcard = false;
  } else {
    feature.creditcard = true;
  }

  const availablePaymentMethods = getPaymentMethods('NZ', feature);

  const { maintenanceCreditCard, hideCreditCard } = useMaintenance({
    paymentMethod,
    country: 'NZ',
  });

  const isRenderCreditCard = () => {
    if (feature.creditcard) {
      return (
        <RadioItem
          label={t('pay.option.creditcard')}
          value="CreditCard"
          data={{
            cy: `paymentOption-${'CreditCard'}`,
          }}
          disabled={hideCreditCard}
          badge={
            hideCreditCard ? (
              <Tooltip
                message={maintenanceCreditCard?.disableMessage?.en}
                id="NZ-CC"
              />
            ) : undefined
          }
        >
          {!hideCreditCard && (
            <PaymentBox
              data={{
                cy: `paymentOption-${'CreditCard'}_content`,
              }}
            >
              <CreditCard />
            </PaymentBox>
          )}
        </RadioItem>
      );
    }
    return <></>;
  };

  return (
    <>
      <Box hidden={hideAllPayments || hidePaymentOptions}>
        <PageSection
          heading={cpqaOnly ? t('payment.details') : t('payment.methods')}
        >
          {invoiceOnly || cpqaOnly ? (
            <Box
              data={{
                cy: `paymentOption-${'Invoice'}`,
              }}
            >
              {cpqaOnly ? <Cpqa /> : <Invoice />}
            </Box>
          ) : (
            <RadioGroup
              id="payment-options"
              value={paymentMethod}
              onChange={({ currentTarget: { value } }) =>
                onRadioChange(
                  availablePaymentMethods.indexOf(value as PaymentMethodType),
                  value,
                )
              }
              label=""
            >
              {isRenderCreditCard()}
              <RadioItem
                label={t('pay.option.invoice')}
                value="Invoice"
                data={{
                  cy: `paymentOption-${'Invoice'}`,
                }}
              >
                <PaymentBox
                  data={{
                    cy: `paymentOption-${'Invoice'}_content`,
                  }}
                >
                  <Invoice />
                </PaymentBox>
              </RadioItem>
            </RadioGroup>
          )}
        </PageSection>
      </Box>

      {errorMessage && (
        <Box marginTop="medium" paddingX={['gutter', 'none']}>
          <TransactionError transactionErrorMessage={errorMessage} />
        </Box>
      )}

      <Box
        paddingTop={hideAllPayments || hidePaymentOptions ? 'none' : 'xlarge'}
        paddingBottom="medium"
      >
        <HirerPaymentTerms
          isAdBudgetSelected={isAdBudgetSelected}
          useCase={useCase}
          site={site}
          isStaging={env === 'staging'}
        />
      </Box>
      <CajaButtons
        isMobileView={isMobileView}
        backButtonComponent={backButtonComponent}
        previewButtonComponent={previewButtonComponent}
        isPaymentInProgress={isPaymentInProgress}
        handlePayAndPostButtonClick={handlePayAndPostButtonClick}
        postAdButtonText={postAdButtonText}
      />
    </>
  );
};

export default SeekJobsNZ;
